export type DestinationBridgeSlug = "morzine" | "croyde" | "boomtown";
export type DestinationBridgeKind = "snow" | "coastal" | "festival";
export type DestinationBridgeSeasonGroup = "snow" | "sun" | "festival";
export type DestinationBridgeJobSpecificity =
  | "destination"
  | "role"
  | "festival";

export type DestinationBridgeJobFilter = {
  seasonGroup: DestinationBridgeSeasonGroup;
  locationIds: readonly string[];
  roleIds: readonly string[];
  specificity: DestinationBridgeJobSpecificity;
  resultLabelSingular: string;
  resultLabelPlural: string;
  emptyHeading: string;
  emptyBody: string;
};

export type DestinationBridgeLink = {
  label: string;
  route: string;
};

export type DestinationBridge = {
  slug: DestinationBridgeSlug;
  name: string;
  kind: DestinationBridgeKind;
  conditionSlug: string | null;
  conditionRoute: string | null;
  guideRoute: string | null;
  guideLabel: string | null;
  jobFilter: DestinationBridgeJobFilter;
  crew: DestinationBridgeLink;
  plannerRoute: string | null;
  plannerLabel: string | null;
  festivalSlug: string | null;
  festivalRoute: string | null;
  festivalLabel: string | null;
  contextSummary: string;
};

export const DESTINATION_BRIDGE_PILOTS = [
  {
    slug: "morzine",
    name: "Morzine",
    kind: "snow",
    conditionSlug: "morzine",
    conditionRoute: "/conditions/morzine",
    guideRoute: "/guides/morzine",
    guideLabel: "Morzine season guide",
    jobFilter: {
      seasonGroup: "snow",
      locationIds: ["morzine"],
      roleIds: [],
      specificity: "destination",
      resultLabelSingular: "Morzine listing",
      resultLabelPlural: "Morzine listings",
      emptyHeading: "No live Morzine listings yet",
      emptyBody:
        "The Morzine jobs filter is ready. Check back as winter employers add roles, or create a profile so employers can find you.",
    },
    crew: {
      label: "Morzine crews",
      route: "/community/crew?q=Morzine&season=winter",
    },
    plannerRoute: "/conditions/morzine/planner",
    plannerLabel: "Plan a day on the mountain",
    festivalSlug: null,
    festivalRoute: null,
    festivalLabel: null,
    contextSummary:
      "Morzine links live snow and MTB conditions with winter jobs, the resort guide, crews and the mountain planner.",
  },
  {
    slug: "croyde",
    name: "Croyde",
    kind: "coastal",
    conditionSlug: "croyde",
    conditionRoute: "/conditions/croyde",
    guideRoute: "/guides/surf-instructor-jobs",
    guideLabel: "Surf instructor guide",
    jobFilter: {
      seasonGroup: "sun",
      locationIds: [],
      roleIds: [
        "surf-instructor",
        "sup-instructor",
        "beach-lifeguard",
        "watersports-manager",
      ],
      specificity: "role",
      resultLabelSingular: "surf and beach role",
      resultLabelPlural: "surf and beach roles",
      emptyHeading: "No live surf and beach roles right now",
      emptyBody:
        "Croyde does not have its own marketplace location leaf yet, so this bridge uses honest surf and beach role filters instead of claiming Croyde-specific supply.",
    },
    crew: {
      label: "Croyde crews",
      route: "/community/crew?q=Croyde&season=summer",
    },
    plannerRoute: "/conditions/croyde#planner=1&session-planner",
    plannerLabel: "Plan a Croyde session",
    festivalSlug: null,
    festivalRoute: null,
    festivalLabel: null,
    contextSummary:
      "Croyde links live surf and coastal conditions with surf work, community search and the session planner.",
  },
  {
    slug: "boomtown",
    name: "Boomtown",
    kind: "festival",
    conditionSlug: null,
    conditionRoute: null,
    guideRoute: "/festivals/boomtown-jobs",
    guideLabel: "Boomtown work guide",
    jobFilter: {
      seasonGroup: "festival",
      locationIds: ["fest-boomtown"],
      roleIds: [],
      specificity: "festival",
      resultLabelSingular: "Boomtown listing",
      resultLabelPlural: "Boomtown listings",
      emptyHeading: "No live Boomtown listings yet",
      emptyBody:
        "The Boomtown jobs filter is ready. The festival page also shows real marketplace supply when listings or candidate availability appear.",
    },
    crew: {
      label: "Boomtown crews",
      route: "/community/crew?q=Boomtown&season=festivals",
    },
    plannerRoute: null,
    plannerLabel: null,
    festivalSlug: "boomtown",
    festivalRoute: "/festivals/boomtown",
    festivalLabel: "Boomtown weather and packing",
    contextSummary:
      "Boomtown links the festival weather page, work guide, festival jobs filter and crew search without inventing a conditions page.",
  },
] as const satisfies readonly DestinationBridge[];

export function destinationJobFilterToQuery(
  filter: DestinationBridgeJobFilter,
): string {
  const parts = [`season=${filter.seasonGroup}`];
  if (filter.roleIds.length > 0) {
    parts.push(`role=${filter.roleIds.join(",")}`);
  }
  if (filter.locationIds.length > 0) {
    parts.push(`location=${filter.locationIds.join(",")}`);
  }
  return parts.join("&");
}

export function buildDestinationJobHref(bridge: DestinationBridge): string {
  return `/jobs?${destinationJobFilterToQuery(bridge.jobFilter)}`;
}

export function findDestinationBridgeBySlug(
  slug: string,
): DestinationBridge | null {
  return (
    DESTINATION_BRIDGE_PILOTS.find((bridge) => bridge.slug === slug) ?? null
  );
}

export function findDestinationBridgeByConditionSlug(
  conditionSlug: string,
): DestinationBridge | null {
  return (
    DESTINATION_BRIDGE_PILOTS.find(
      (bridge) => bridge.conditionSlug === conditionSlug,
    ) ?? null
  );
}

export function findDestinationBridgeByFestivalSlug(
  festivalSlug: string,
): DestinationBridge | null {
  return (
    DESTINATION_BRIDGE_PILOTS.find(
      (bridge) => bridge.festivalSlug === festivalSlug,
    ) ?? null
  );
}

export function findDestinationBridgeByJobLocationIds(
  locationIds: readonly string[],
): DestinationBridge | null {
  const locationSet = new Set(locationIds);
  return (
    DESTINATION_BRIDGE_PILOTS.find((bridge) =>
      bridge.jobFilter.locationIds.some((locationId) =>
        locationSet.has(locationId),
      ),
    ) ?? null
  );
}
